import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  HeaderTemplate,
  SectionHeader,
  Stepper,
  TitleHeader,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import {
  UploadedFileModel
} from '../../../api-refactored/special-consideration/useGetUploadedFileInfo';
import {
  usePutSpecialConsideration
} from '../../../api-refactored/special-consideration/usePutSpecialConsideration';
import {
  useGetSpecialConsiderationReview
} from '../../../api/candidate/special-consideration/@candidate/queries-services/queries';
import Declaration from '../../../components/Declaration';
import DocumentBadge from '../../../components/DocumentBadge';
import FormHeader from '../../../components/form-items/FormHeader';
import LoadingComponent from '../../../components/LoadingComponent';
import DraftMessage from '../../../modules/specialConsideration/components/DraftMessage';
import ExamLevelDetails from '../../../modules/specialConsideration/components/ExamLevelDetails';
import PersonalDetails from '../../../modules/specialConsideration/components/PersonalDetails';
import SubjectTable from '../../../modules/specialConsideration/components/SubjectTable';
import RouteKey from '../../../routes/key';

const { applicationSummary, uploadSupportingDocument, amendUploadSupportingDocument } =
  RouteKey['special-consideration'].child;

interface ReviewApplicationPageProps {
  title: string;
  routeSteps: { label: string }[];
  amendment: boolean;
}

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-6.5%',
  },
  documentBadgeContainer: {
    marginBottom: theme.spacing(4),
  },
  fileContainer: {
    marginBottom: theme.spacing(8),
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  submitButton: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(8),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
  },
  submittedDocuments: {
    marginBottom: theme.spacing(1),
  },
  keyLabel: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
}));

const ReviewApplication = ({ title, routeSteps, amendment }: ReviewApplicationPageProps) => {
  const { classes } = useStyles();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<boolean>(false);
  const { snackbarHandler } = useSnackbarHandler();
  const [allowProceed, setAllowProceed] = useState(false);

  const { data: specialConsiderationReviewData } =
    useGetSpecialConsiderationReview(applicationUuid);

  const submitApplication = usePutSpecialConsideration({
    uuid: applicationUuid,
    onSuccess: () => {
      history.push(`${applicationSummary.route}`);
    },
    onError: (err) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: err.response?.data.message,
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    },
  });

  const handleAgreement = () => {
    setErrMsg(false);
    setIsDeclared(!isDeclared);
  };

  /** to handle go back previous page/action
   * */
  const onBack = () => {
    if (amendment) {
      history.push(`${amendUploadSupportingDocument.route}`);
    } else {
      history.push(`${uploadSupportingDocument.route}`);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSubmitApplication = () => {
    if (!isDeclared) {
      setErrMsg(true);
      return;
    }
    setAllowProceed(true);
    submitApplication.mutate();
  };

  if (!specialConsiderationReviewData) {
    return <LoadingComponent />;
  }

  const AddedDocumentsDisplay = () =>
    specialConsiderationReviewData?.fileNames
      .filter((item: UploadedFileModel) => !item.isSubmitted)
      .map((item: UploadedFileModel) => (
        <DocumentBadge
          className={classes.documentBadgeContainer}
          documentName={item.fileName}
          key={item.fileUuid}
        />
      ));

  const SubmittedDocumentsDisplay = () =>
    specialConsiderationReviewData?.fileNames
      .filter((item: UploadedFileModel) => item.isSubmitted)
      .map((item: UploadedFileModel) => (
        <DocumentBadge
          className={classes.documentBadgeContainer}
          documentName={item.fileName}
          key={item.fileUuid}
        />
      ));

  const DocumentDisplayNonAmendment = () =>
    specialConsiderationReviewData?.fileNames.map((item: UploadedFileModel) => (
      <DocumentBadge
        className={classes.documentBadgeContainer}
        documentName={item.fileName}
        key={item.fileUuid}
      />
    ));

  return (
    <>
      <Prompt
        when={!allowProceed}
        message={t(
          "specialConsideration->>ApplyForSpecialConsideration.ReminderToSubmitPopUp"
        )}
      />
      <TitleHeader title={title} />
      <HeaderTemplate>
        <div className={classes.stepper}>
          <Stepper activeStep={2} steps={routeSteps} />
        </div>
      </HeaderTemplate>

      <ContentTemplate type="full" isContinuable isDividerBottom>
        <SectionHeader
          title={t('specialConsideration->>ApplyForSpecialConsideration.Review Application')}
        />
        <PersonalDetails {...specialConsiderationReviewData?.candidateBasicInfoModel} />
        <Divider />
        <ExamLevelDetails
          formHeader={t('specialConsideration->>ApplyForSpecialConsideration.ExamDetails')}
          {...specialConsiderationReviewData?.basicExamInfoModel}
        />
        <Divider />
        <SubjectTable data={specialConsiderationReviewData?.selectionModels} />
        <Divider />

        <FormHeader
          title={t('specialConsideration->>ApplyForSpecialConsideration.SupportingDocuments')}
        />
        <Box className={classes.fileContainer}>
          {amendment ? (
            <>
              <Box className={classes.submittedDocuments}>
                <Typography variant="h5" className={classes.keyLabel}>
                  {t('specialConsideration->>ApplyForSpecialConsideration.AddedDocuments')}
                </Typography>
                {AddedDocumentsDisplay()}
              </Box>
              <Typography variant="h5" className={classes.keyLabel}>
                {t('specialConsideration->>ApplyForSpecialConsideration.SubmittedDocuments')}
              </Typography>
              {SubmittedDocumentsDisplay()}
            </>
          ) : (
            <>{DocumentDisplayNonAmendment()}</>
          )}
        </Box>
      </ContentTemplate>

      <ContentTemplate type="large" continuous>
        <FormHeader title={t('appeal:ReviewAppeal.Declaration')} />
        <Declaration
          noTitle
          isAgreed={isDeclared}
          handleAgreement={handleAgreement}
          customContent={t(`specialConsideration->>ApplyForSpecialConsideration.DeclarationText`)}
        />
        {errMsg && (
          <Grid xs={12} md={8}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {t('components->>Declaration.Please confirm declaration before you proceed.')}
            </Typography>
          </Grid>
        )}

        <Box className={classes.buttonContainer}>
          <Button
            id="button-review-application-back"
            className={classes.backButton}
            variant="outlined"
            onClick={() => {
              onBack();
            }}
          >
            {t('specialConsideration->>ApplyForSpecialConsideration.Back')}
          </Button>

          <ApiButton
            id="button-submit"
            className={classes.submitButton}
            status={submitApplication.status}
            type="submit"
            onClick={handleSubmitApplication}
          >
            {t('specialConsideration->>ApplyForSpecialConsideration.Submit')}
          </ApiButton>
          <DraftMessage />
        </Box>
      </ContentTemplate>
    </>
  );
};

export default ReviewApplication;
